// ═══════════════════════════════════════════════════════════════════════════
// PAGE ADMIN — COMITÉS + RISK LISTS INDEX
// Migration S4.2 du plan TS+Vite (2026-05-10) : .js → .ts strict.
// ═══════════════════════════════════════════════════════════════════════════
//
// Référentiel paramétrable par tenant. Pré-seed au provisioning.
// Suppression bloquée si comité utilisé par au moins une action.

import { navigateTo } from '../router.js';
import { buildCommitteesTableHtml, type CommitteeSortKey } from './page-committees-fragments.js';

export interface CommitteeEntity {
  id: number;
  code: string;
  name: string;
  position?: number;
  actions_count?: number;
  in_use?: boolean;
  invite_pilot?: boolean;
  alert_actions_update_days?: number;
  alert_support_prep_days?: number;
  alert_cr_extract_days?: number;
}

interface RiskListSummary {
  id: number;
  name: string;
  description?: string | null;
  risk_count: number;
}

declare global {
  interface Window {
    renderCommitteesPage: typeof renderCommitteesPage;
    showCommitteeModal: typeof showCommitteeModal;
    renderRiskListsPage: typeof renderRiskListsPage;
    showRiskListModal: typeof showRiskListModal;
  }
}

// Les routes de ce fichier sont gardées par `<domaine>.read`, de simples clés
// de menu qui n'autorisent pas l'écriture (revue front §I3).
const canWriteCommittees = (): boolean =>
  !!(window.hasPermission && window.hasPermission('committees.write'));
const canWriteRiskLists = (): boolean =>
  !!(window.hasPermission && window.hasPermission('risk_lists.write'));

pages.adminCommittees = async function () {
  setHeader(
    t('vocabulary.committee_plural'),
    canWriteCommittees()
      ? `<button class="btn btn-primary ml-auto" id="btn-new-committee">${esc(t('committees.btn.add'))}</button>`
      : '',
  );
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);
  try {
    const committees = (await loadCommittees(true)) as CommitteeEntity[];
    renderCommitteesPage(committees);
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('common.error');
    setContent(`<div class="error">${esc(msg)}</div>`);
  }
};

function renderCommitteesPage(committees: CommitteeEntity[]): void {
  let sortKey: CommitteeSortKey = 'position';
  let sortDir: 'asc' | 'desc' = 'asc';

  // Tri et table : rendu pur délégué à page-committees-fragments (l'état de
  // tri et le droit d'écriture lui sont passés au moment du rendu).
  const renderTable = (): string =>
    buildCommitteesTableHtml(committees, sortKey, sortDir, canWriteCommittees());

  const reRender = (): void => {
    const card = document.getElementById('committees-card');
    if (card) {
      card.outerHTML = renderTable();
      wireRows();
      initDragDrop();
    }
  };

  setContent(renderTable());
  wireRows();
  initDragDrop();

  function initDragDrop(): void {
    const tbody = document.getElementById('committees-tbody');
    if (!tbody) return;
    if (sortKey !== 'position' || sortDir !== 'asc') return;

    let dragSrc: HTMLElement | null = null;
    tbody.addEventListener('dragstart', (e: DragEvent) => {
      const target = e.target as HTMLElement | null;
      dragSrc = target?.closest<HTMLElement>('tr.committee-drag-row') ?? null;
      if (!dragSrc) return;
      dragSrc.style.opacity = '0.4';
      if (e.dataTransfer) {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', dragSrc.dataset['committeeId'] || '');
      }
    });
    tbody.addEventListener('dragend', (e: DragEvent) => {
      const row = (e.target as HTMLElement | null)?.closest<HTMLElement>('tr.committee-drag-row');
      if (row) row.style.opacity = '';
      tbody
        .querySelectorAll('tr.committee-drag-row')
        .forEach((r) => r.classList.remove('drag-over'));
    });
    tbody.addEventListener('dragover', (e: DragEvent) => {
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
      const target = (e.target as HTMLElement | null)?.closest<HTMLElement>(
        'tr.committee-drag-row',
      );
      tbody
        .querySelectorAll('tr.committee-drag-row')
        .forEach((r) => r.classList.remove('drag-over'));
      if (target && target !== dragSrc) target.classList.add('drag-over');
    });
    tbody.addEventListener('dragleave', (e: DragEvent) => {
      const target = (e.target as HTMLElement | null)?.closest<HTMLElement>(
        'tr.committee-drag-row',
      );
      if (target) target.classList.remove('drag-over');
    });
    tbody.addEventListener('drop', (e: DragEvent) => {
      e.preventDefault();
      const target = (e.target as HTMLElement | null)?.closest<HTMLElement>(
        'tr.committee-drag-row',
      );
      tbody
        .querySelectorAll('tr.committee-drag-row')
        .forEach((r) => r.classList.remove('drag-over'));
      if (!target || !dragSrc || target === dragSrc) return;
      const rows = [...tbody.querySelectorAll<HTMLElement>('tr.committee-drag-row')];
      const srcIdx = rows.indexOf(dragSrc);
      const tgtIdx = rows.indexOf(target);
      if (srcIdx < tgtIdx) tbody.insertBefore(dragSrc, target.nextSibling);
      else tbody.insertBefore(dragSrc, target);
      dragSrc.style.opacity = '';
      const newOrder = [...tbody.querySelectorAll<HTMLElement>('tr.committee-drag-row')].map((r) =>
        parseInt(r.dataset['committeeId'] || '0'),
      );
      void (async () => {
        try {
          const updated = (await api.reorderCommittees(newOrder)) as CommitteeEntity[];
          committees.length = 0;
          committees.push(...updated);
          setCommitteesCache(committees);
          toast(t('committees.toast.reordered'));
          reRender();
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : t('common.error');
          toast(msg, 'error');
        }
      })();
    });
  }

  function wireRows(): void {
    const card = document.getElementById('committees-card');
    if (!card) return;
    card.addEventListener('click', (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const sortTh = target.closest<HTMLElement>('[data-sort-comm]');
      if (sortTh) {
        const key = sortTh.dataset['sortComm'] as CommitteeSortKey;
        if (sortKey === key) sortDir = sortDir === 'asc' ? 'desc' : 'asc';
        else {
          sortKey = key;
          sortDir = 'asc';
        }
        reRender();
        return;
      }
      const clickCell = target.closest<HTMLElement>('[data-click-comm]');
      if (clickCell && !target.closest('button')) {
        const id = parseInt(clickCell.dataset['clickComm'] || '0');
        if (id) navigateTo('admin/committees/:id', { id });
        return;
      }
      const editBtn = target.closest<HTMLElement>('[data-edit-comm]');
      if (editBtn) {
        // Édition = fiche complète (identité, relances, guides de réunion).
        // La modale ne sert plus qu'à la création rapide (août 2026).
        const id = parseInt(editBtn.dataset['editComm'] || '0');
        if (id) navigateTo('admin/committees/:id', { id });
        return;
      }
      const delBtn = target.closest<HTMLElement>('[data-del-comm]');
      if (delBtn) {
        const id = parseInt(delBtn.dataset['delComm'] || '0');
        const name = delBtn.dataset['commName'] || '';
        const used = delBtn.dataset['commUsed'] === '1';
        if (used) {
          openModal(
            t('committees.modal.delete_blocked.title'),
            `<p>${t('committees.modal.delete_blocked.message', { name })}</p>`,
            () => closeModal(),
            'OK',
          );
          return;
        }
        openModal(
          t('committees.modal.delete.title', { object: t('vocabulary.committee') }),
          `<p>${t('committees.modal.delete.message', { object: t('vocabulary.committee'), name })}</p>`,
          async () => {
            try {
              await api.deleteCommittee(id);
              closeModal();
              toast(t('committees.toast.deleted'));
              committees.splice(
                committees.findIndex((x) => x.id === id),
                1,
              );
              setCommitteesCache(committees);
              reRender();
            } catch (err: unknown) {
              const msg = err instanceof Error ? err.message : t('common.error');
              toast(msg, 'error');
            }
          },
          t('common.delete'),
        );
      }
    });
    document.getElementById('btn-new-committee')?.addEventListener('click', () => {
      showCommitteeModal((created) => {
        committees.push(created);
        setCommitteesCache(committees);
        reRender();
      });
    });
  }
}

/**
 * Modale de CRÉATION d'un comité — code et libellé, rien de plus.
 *
 * Août 2026 : l'édition est passée en page pleine
 * (`#admin/committees/:id`), la configuration ayant dépassé ce qu'une popup
 * peut porter — relances, invitation des pilotes, guides de réunion.
 * On crée vite ici, on configure ensuite sur la fiche.
 */
function showCommitteeModal(onSaved: (saved: CommitteeEntity) => void): void {
  const body = `
    <div class="form-row">
      <div class="form-group" style="flex:0 0 140px;min-width:120px">
        <label for="cm-code">${esc(t('committees.field.code.label'))}</label>
        <input type="text" id="cm-code" maxlength="5" value=""
          placeholder="${esc(t('committees.field.code.placeholder'))}"
          style="width:100%;text-transform:uppercase;font-family:var(--font-mono)">
      </div>
      <div class="form-group" style="flex:1">
        <label for="cm-name">${esc(t('committees.col.label'))}</label>
        <input type="text" id="cm-name" value="" style="width:100%"
          placeholder="${esc(t('committees.field.label.placeholder'))}">
      </div>
    </div>
    <p style="font-size:11px;color:var(--text3);margin:4px 0 0">
      ${esc(t('committees.modal.create.help'))}
    </p>`;

  openModal(
    t('committees.modal.create.title', { object: t('vocabulary.committee') }),
    body,
    async () => {
      const code =
        (document.getElementById('cm-code') as HTMLInputElement | null)?.value
          .trim()
          .toUpperCase() || '';
      const name =
        (document.getElementById('cm-name') as HTMLInputElement | null)?.value.trim() || '';
      if (!code || !name) {
        toast(t('committees.error.code_label_required'), 'error');
        return;
      }
      try {
        const result = (await api.createCommittee({ code, name })) as CommitteeEntity;
        closeModal();
        toast(t('committees.toast.created'));
        onSaved(result);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : t('common.error');
        toast(msg, 'error');
      }
    },
    t('common.add_short'),
  );

  setTimeout(() => {
    document.getElementById('cm-code')?.addEventListener('input', (e: Event) => {
      const target = e.target as HTMLInputElement | null;
      if (target) target.value = target.value.toUpperCase();
    });
  }, 0);
}

pages.adminRiskLists = async function () {
  setHeader(
    t('risk_lists_index.page_title', { risk_plural: t('vocabulary.risk_plural') }),
    canWriteRiskLists()
      ? `<button class="btn btn-secondary ml-auto" id="btn-import-rl">${esc(t('risk_lists_index.btn.import'))}</button>
     <button class="btn btn-primary" id="btn-new-list" style="margin-left:8px">${esc(t('risk_lists.btn.add'))}</button>`
      : '',
  );
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);
  try {
    const lists = (await loadRiskLists(true)) as RiskListSummary[];
    renderRiskListsPage(lists);
    document
      .getElementById('btn-new-list')
      ?.addEventListener('click', () => showRiskListModal(null));
    document.getElementById('btn-import-rl')?.addEventListener('click', () => {
      const examples = window.EXPORT_EXAMPLES['risk-lists'];
      window.showExportImportModal(
        'risk-lists',
        t('risk_lists.parent_title'),
        examples?.json ?? '',
        examples?.csv ?? '',
        { importOnly: true },
      );
    });
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('common.error');
    setContent(`<div class="error">${esc(msg)}</div>`);
  }
};

function renderRiskListsPage(lists: RiskListSummary[]): void {
  if (!lists.length) {
    setContent(`<div class="empty">${t('risk_lists_index.empty')}</div>`);
    return;
  }
  let sortKey: 'name' | 'desc' | 'risks' = 'name';
  let sortDir: 'asc' | 'desc' = 'asc';

  const sortLists = (arr: ReadonlyArray<RiskListSummary>): RiskListSummary[] =>
    [...arr].sort((a, b) => {
      let va: string | number;
      let vb: string | number;
      switch (sortKey) {
        case 'name':
          va = (a.name || '').toLowerCase();
          vb = (b.name || '').toLowerCase();
          break;
        case 'desc':
          va = (a.description || '').toLowerCase();
          vb = (b.description || '').toLowerCase();
          break;
        case 'risks':
          va = a.risk_count || 0;
          vb = b.risk_count || 0;
          break;
        default:
          va = (a.name || '').toLowerCase();
          vb = (b.name || '').toLowerCase();
      }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

  const th = (key: 'name' | 'desc' | 'risks', label: string): string => {
    const active = sortKey === key;
    const arrow = active ? (sortDir === 'asc' ? '↑' : '↓') : '↕';
    return `<th class="sortable${active ? ' sorted' : ''}" data-sort-list="${key}" style="cursor:pointer;user-select:none">${esc(label)} <span class="sort-arrow" style="font-size:10px">${arrow}</span></th>`;
  };

  const buildRows = (): string =>
    sortLists(lists)
      .map(
        (l) => `<tr>
      <td style="cursor:${canWriteRiskLists() ? 'pointer' : 'default'}" ${canWriteRiskLists() ? `data-click-list="${l.id}"` : ''}><strong>${esc(l.name)}</strong></td>
      <td>${esc(l.description || '—')}</td>
      <td>${l.risk_count}</td>
      <td style="white-space:nowrap">
        <a href="#admin/risk-lists/${l.id}" class="btn btn-secondary btn-sm">${esc(t('risk_lists_index.btn.manage'))}</a>${
          // « Gérer » navigue vers le détail (lecture) et reste offert ; ✎ et ✕
          // écrivent (revue front §I3).
          canWriteRiskLists()
            ? `
        <button class="btn btn-secondary btn-icon ml-1" data-edit-list="${l.id}" title="${esc(t('common.edit'))}">✎</button>
        <button class="btn btn-danger btn-icon ml-1" data-del-list="${l.id}" data-list-name="${esc(l.name)}" title="${esc(t('common.delete'))}">✕</button>`
            : ''
        }
      </td>
    </tr>`,
      )
      .join('');

  setContent(`<div class="card" id="lists-card"><div class="table-wrap"><table>
    <thead><tr>${th('name', t('risk_lists_index.col.name'))}${th('desc', t('risk_lists_index.col.desc'))}${th('risks', t('vocabulary.risk_plural'))}<th></th></tr></thead>
    <tbody id="lists-tbody">${buildRows()}</tbody>
  </table></div></div>`);

  document.getElementById('lists-card')?.addEventListener('click', (e: Event) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;

    const sortTh = target.closest<HTMLElement>('[data-sort-list]');
    if (sortTh) {
      const key = sortTh.dataset['sortList'] as 'name' | 'desc' | 'risks';
      if (sortKey === key) sortDir = sortDir === 'asc' ? 'desc' : 'asc';
      else {
        sortKey = key;
        sortDir = 'asc';
      }
      const tbody = document.getElementById('lists-tbody');
      if (tbody) tbody.innerHTML = buildRows();
      document.querySelectorAll<HTMLElement>('[data-sort-list]').forEach((h) => {
        const a = h.dataset['sortList'] === sortKey;
        const arrow = h.querySelector('.sort-arrow');
        if (arrow) arrow.textContent = a ? (sortDir === 'asc' ? '↑' : '↓') : '↕';
        h.classList.toggle('sorted', a);
      });
      return;
    }
    if (target.tagName === 'A') return;
    const clickCell = target.closest<HTMLElement>('[data-click-list]');
    if (clickCell && !target.closest('button')) {
      const id = parseInt(clickCell.dataset['clickList'] || '0');
      const l = lists.find((x) => x.id === id);
      if (l) showRiskListModal(l);
      return;
    }
    const editBtn = target.closest<HTMLElement>('[data-edit-list]');
    if (editBtn) {
      const id = parseInt(editBtn.dataset['editList'] || '0');
      const l = lists.find((x) => x.id === id);
      if (l) showRiskListModal(l);
      return;
    }
    const delBtn = target.closest<HTMLElement>('[data-del-list]');
    if (delBtn) {
      const id = parseInt(delBtn.dataset['delList'] || '0');
      const name = delBtn.dataset['listName'] || '';
      openModal(
        t('risk_lists_index.modal.delete.title'),
        `<p>${t('risk_lists_index.modal.delete.message', { name })}</p>`,
        async () => {
          try {
            await api.deleteRiskList(id);
            closeModal();
            toast(t('risk_lists_index.toast.deleted'));
            _cache.riskLists = null;
            void pages.adminRiskLists?.({});
          } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : t('common.error');
            toast(msg, 'error');
          }
        },
        t('common.delete'),
      );
    }
  });
  document.getElementById('btn-new-list')?.addEventListener('click', () => showRiskListModal(null));
}

function showRiskListModal(list: RiskListSummary | null): void {
  const isNew = !list;
  const body = `
    <div class="form-group"><label>${esc(t('common.field.name'))}</label><input type="text" id="lm-name" value="${esc(list ? list.name : '')}" placeholder="${esc(t('risk_lists_index.field.name.placeholder'))}"></div>
    <div class="form-group" style="margin-top:8px"><label>${esc(t('risk_lists_index.field.desc'))}</label><textarea id="lm-desc" rows="2" placeholder="${esc(t('risk_lists_index.field.desc.placeholder'))}">${esc(list ? list.description || '' : '')}</textarea></div>`;
  openModal(
    isNew ? t('risk_lists_index.modal.create.title') : t('risk_lists_index.modal.update.title'),
    body,
    async () => {
      const nameEl = document.getElementById('lm-name') as HTMLInputElement | null;
      const descEl = document.getElementById('lm-desc') as HTMLTextAreaElement | null;
      const name = nameEl?.value.trim() || '';
      if (!name) {
        toast(t('common.error.name_required'), 'error');
        return;
      }
      const desc = descEl?.value || '';
      try {
        if (isNew) await api.createRiskList({ name, description: desc });
        else if (list) await api.updateRiskList(list.id, { name, description: desc });
        closeModal();
        toast(isNew ? t('risk_lists_index.toast.created') : t('risk_lists_index.toast.updated'));
        _cache.riskLists = null;
        void pages.adminRiskLists?.({});
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : t('common.error');
        toast(msg, 'error');
      }
    },
    isNew ? t('common.add_short') : t('common.save'),
  );
}

// ── ESM globals shim (lot B.8 + S4.2) ──
window.renderCommitteesPage = renderCommitteesPage;
window.showCommitteeModal = showCommitteeModal;
window.renderRiskListsPage = renderRiskListsPage;
window.showRiskListModal = showRiskListModal;

export {};
